<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Check, X } from "@lucide/vue";

import { Button, Input } from "@/shared/ui";
import { toast } from "@/shared/lib";

import { useKeyboardAwareFocus } from "../model/useKeyboardAwareFocus";
import { useServicesCatalogStore } from "../model/store";

const { t } = useI18n();
const store = useServicesCatalogStore();

const { onFocusInput, onBlurInput } = useKeyboardAwareFocus();

const text = ref("");
const pending = ref(false);
const inputRef = ref<{ $el: HTMLInputElement } | null>(null);

onMounted(() => {
  inputRef.value?.$el.focus();
});

async function submit(): Promise<void> {
  const value = text.value.trim();
  if (!value || pending.value) return;
  pending.value = true;
  try {
    await store.createCategory(value);
    text.value = "";
    store.closeCategoryDraft();
  } catch {
    toast.error(t("errors.server"));
  } finally {
    pending.value = false;
  }
}
</script>

<template>
  <div
    class="bg-accent rounded-md border border-border px-3 py-2 mb-4"
    data-testid="category-draft-row"
  >
    <div class="flex items-center gap-2">
      <Input
        ref="inputRef"
        v-model="text"
        data-testid="category-draft-input"
        :placeholder="t('services.categories.addPlaceholder')"
        :aria-label="t('services.categories.addPlaceholder')"
        autocomplete="off"
        @focus="onFocusInput"
        @blur="onBlurInput"
        @keydown.enter.prevent="submit"
        @keydown.esc.prevent="store.closeCategoryDraft()"
      />
      <Button
        variant="ghost"
        size="icon"
        class="max-sm:size-11"
        data-testid="category-draft-submit"
        :disabled="!text.trim() || pending"
        :loading="pending"
        :aria-label="t('services.actions.confirm')"
        @click="submit"
      >
        <Check :size="20" aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        class="max-sm:size-11"
        data-testid="category-draft-cancel"
        :aria-label="t('services.actions.cancel')"
        @click="store.closeCategoryDraft()"
      >
        <X :size="20" aria-hidden="true" />
      </Button>
    </div>
  </div>
</template>
